import { BindCard } from '@components/bindCard/BindCard';
import { Box, Grid, Typography } from '@mui/material';
import { Checkbox } from '@components/checkbox';
import { Table } from '@components/table/Table';
import { Button } from '@components/button';
import { useState, useEffect } from 'react';
import { shallow } from 'zustand/shallow';
import { useStore } from '@store/GlobalStore';
import { useSaveFields } from '@pages/QuoteDetails/helpers/useSaveFields';
import { useQuoteRatingJob } from '@pages/BindQuote/helpers/useQuoteRatingJob';
import { getExcessPremiumColumns } from '@pages/BindQuote/ExcessPremiumPanel/getExcessPremiumColumns';
import { useQuoteStore } from '@store/Quote/QuoteStore';

export const ExcessPremiumPanel = ({
  attributeTypes,
  quoteId,
  setIsRating,
  policyQuoteItemId,
}: {
  attributeTypes: Record<
    string,
    {
      id: string;
      isActive: boolean;
      defaultValue?: boolean;
      attribute: { id: string; value: number | boolean; isLocked: boolean };
    }
  >;
  quoteId: string;
  setIsRating: (isRating: boolean) => void;
  policyQuoteItemId: string;
}) => {
  const [userAuth] = useStore((state) => [state.userAuth], shallow);
  const [disableSaveExcess, setDisableSaveExcess] = useState(false);
  const isInternalUser = userAuth.isInternalUser;

  const [showFieldsAutoChangedWarning, setShowFieldsAutoChangedWarning] = useState(false);

  const { saveFields, isLoading: isSaveFieldsLoading } = useSaveFields({
    quoteId,
    quoteItemId: policyQuoteItemId,
    fields: [
      {
        attributeTypeId: attributeTypes.EXEachEventLimit.id,
        attributeId: attributeTypes.EXEachEventLimit.attribute?.id,
      },
      {
        attributeTypeId: attributeTypes.EXAggregateLimit.id,
        attributeId: attributeTypes.EXAggregateLimit.attribute?.id,
      },
      {
        attributeTypeId: attributeTypes.EXPolicyTerrorismCoverageInd.id,
        attributeId: attributeTypes.EXPolicyTerrorismCoverageInd.attribute?.id,
      },
      // need all the print limits
      {
        attributeTypeId: attributeTypes.EXEstimatedPremium1MSelectedLimitPrint?.id,
        attributeId: attributeTypes.EXEstimatedPremium1MSelectedLimitPrint.attribute?.id,
      },
      {
        attributeTypeId: attributeTypes.EXEstimatedPremium2MSelectedLimitPrint?.id,
        attributeId: attributeTypes.EXEstimatedPremium2MSelectedLimitPrint.attribute?.id,
      },
      {
        attributeTypeId: attributeTypes.EXEstimatedPremium3MSelectedLimitPrint?.id,
        attributeId: attributeTypes.EXEstimatedPremium3MSelectedLimitPrint.attribute?.id,
      },
      {
        attributeTypeId: attributeTypes.EXEstimatedPremium4MSelectedLimitPrint?.id,
        attributeId: attributeTypes.EXEstimatedPremium4MSelectedLimitPrint.attribute?.id,
      },
      {
        attributeTypeId: attributeTypes.EXEstimatedPremium5MSelectedLimitPrint?.id,
        attributeId: attributeTypes.EXEstimatedPremium5MSelectedLimitPrint.attribute?.id,
      },
    ],
  });

  const { startRating, isCreateRatingLoading } = useQuoteRatingJob({ quoteId, setIsRating });

  const [excessCoverage, setExcessCoverage] = useState(attributeTypes.EXEachEventLimit.attribute?.value),
    [terrorismAcceptance, setTerrorismAcceptance] = useState(
      attributeTypes.EXPolicyTerrorismCoverageInd.attribute?.value,
    ),
    [setAttributeValue, clearAttributeValues, setAttributeValues, attributesById, resetStore] = useQuoteStore(
      (state) => [
        state.setAttributeValue,
        state.clearAttributeValues,
        state.setAttributeValues,
        state.attributesById,
        state.resetStore,
      ],
      shallow,
    ),
    updateExcessCoverage = (newCoverage: number, printLimitAttributeKey: string) => {
      setExcessCoverage(newCoverage);

      const printLimitAttribute = attributeTypes[printLimitAttributeKey];

      if (newCoverage === attributeTypes.EXEachEventLimit.attribute?.value) {
        clearAttributeValues([
          attributeTypes.EXEachEventLimit.attribute?.id,
          attributeTypes.EXAggregateLimit.attribute?.id,
        ]);

        if (isInternalUser) {
          // fields are not visible to non internal users
          setAttributeValue(printLimitAttribute?.attribute?.id, {
            isLocked: printLimitAttribute?.attribute?.isLocked,
            value: true,
            valueOverride: null,
          });
        }
        return;
      }

      setAttributeValues([
        {
          attributeId: attributeTypes.EXEachEventLimit.attribute?.id,
          update: {
            isLocked: attributeTypes.EXEachEventLimit.attribute?.isLocked,
            value: newCoverage,
            valueOverride: null,
          },
        },
        {
          attributeId: attributeTypes.EXAggregateLimit.attribute?.id,
          update: {
            isLocked: attributeTypes.EXAggregateLimit.attribute?.isLocked,
            value: newCoverage,
            valueOverride: null,
          },
        },
        // need to turn the print limit on if this is checked
        {
          attributeId: printLimitAttribute?.attribute?.id,
          update: {
            isLocked: printLimitAttribute?.attribute?.isLocked,
            value: true,
            valueOverride: null,
          },
        },
      ]);
    },
    updatePrintLimit = (attributeKey: string, newValue: boolean) => {
      const selectedAttribute = attributeTypes[attributeKey];

      setAttributeValue(selectedAttribute?.attribute?.id, {
        isLocked: selectedAttribute?.attribute?.isLocked,
        value: newValue,
        valueOverride: null,
      });
    },
    updateTerrorismAcceptance = (newTerrorismAcceptance: boolean) => {
      setTerrorismAcceptance(newTerrorismAcceptance);
      if (newTerrorismAcceptance === attributeTypes.EXPolicyTerrorismCoverageInd.attribute?.value) {
        clearAttributeValues([attributeTypes.EXPolicyTerrorismCoverageInd.attribute?.id]);
        return;
      }
      setAttributeValue(attributeTypes.EXPolicyTerrorismCoverageInd.attribute?.id, {
        isLocked: attributeTypes.EXPolicyTerrorismCoverageInd.attribute?.isLocked,
        value: newTerrorismAcceptance,
        valueOverride: null,
      });
    };

  useEffect(() => {
    // on mount, reset the store to avoid any stale data
    resetStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const excessOptionsColumns = getExcessPremiumColumns({
    excessCoverage: excessCoverage as number,
    updateExcessCoverage,
    updatePrintLimit,
    isInternalUser,
  });

  useEffect(() => {
    const printLimitsNotDefined = attributeTypes.EXEstimatedPremium1MSelectedLimitPrint?.attribute?.value === undefined;

    if (!printLimitsNotDefined || !isInternalUser) {
      return;
    }

    const selectedEXEachEventLimit = attributeTypes.EXEachEventLimit.attribute?.value;

    let printLimitAttribute = attributeTypes.EXEstimatedPremium1MSelectedLimitPrint;
    if (selectedEXEachEventLimit === 2_000_000) {
      printLimitAttribute = attributeTypes.EXEstimatedPremium2MSelectedLimitPrint;
    } else if (selectedEXEachEventLimit === 3_000_000) {
      printLimitAttribute = attributeTypes.EXEstimatedPremium3MSelectedLimitPrint;
    } else if (selectedEXEachEventLimit === 4_000_000) {
      printLimitAttribute = attributeTypes.EXEstimatedPremium4MSelectedLimitPrint;
    } else if (selectedEXEachEventLimit === 5_000_000) {
      printLimitAttribute = attributeTypes.EXEstimatedPremium5MSelectedLimitPrint;
    }

    // trigger attribute update to set corresponding one to on, this will block the form until saved
    setAttributeValue(printLimitAttribute?.attribute?.id, {
      isLocked: printLimitAttribute?.attribute?.isLocked,
      value: true,
      valueOverride: null,
    });

    setShowFieldsAutoChangedWarning(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributeTypes, isInternalUser]);

  const data = [1, 2, 3, 4, 5]
    .map((limit) => {
      const limitKey = `EXEstimatedPremium${limit}MSelectedLimit`;
      const printLimitKey = `EXEstimatedPremium${limit}MSelectedLimitPrint`;
      const limitData = attributeTypes[limitKey];

      const printLimitData = attributeTypes[printLimitKey];
      if (limitData && limitData.isActive) {
        const printLimitID = printLimitData?.attribute?.id;

        const printLimitSelectedValue = attributesById[printLimitID]?.value;

        return {
          id: limitData.id,
          eachEvent: limit * 1_000_000,
          estimatedPremiumWTerrorism: Math.round((limitData.attribute?.value as number) * 1.03),
          estimatedPremiumWOTerrorism: Math.round(limitData.attribute?.value as number),
          printLimitKey,
          limitPrint:
            printLimitSelectedValue !== undefined
              ? printLimitSelectedValue
              : attributeTypes[printLimitKey]?.attribute?.value !== undefined
                ? attributeTypes[printLimitKey]?.attribute?.value
                : false,
        };
      }
    })
    .filter(Boolean);

  const handleSave = () => {
    setDisableSaveExcess(true);
    saveFields([], false).then(async () => {
      await startRating();
      setShowFieldsAutoChangedWarning(false);
      setDisableSaveExcess(false);
    });
  };

  const handleRemoveExcess = () => {
    setDisableSaveExcess(true);
    const excessAttribute = attributeTypes.LOIExcessLiabilityInd;

    saveFields(
      [
        {
          id: excessAttribute.attribute?.id,
          attributeTypeId: excessAttribute.id,
          value: false,
        },
      ],
      false,
    ).then(async () => {
      startRating()
        .then(() => setDisableSaveExcess(false))
        .catch(() => setDisableSaveExcess(false));
    });
  };

  const isGeneralLiabilityIndOn = attributeTypes.LOIGeneralLiabilityInd?.attribute?.value;

  const printLimitAttributeKeys = [
    'EXEstimatedPremium1MSelectedLimitPrint',
    'EXEstimatedPremium2MSelectedLimitPrint',
    'EXEstimatedPremium3MSelectedLimitPrint',
    'EXEstimatedPremium4MSelectedLimitPrint',
    'EXEstimatedPremium5MSelectedLimitPrint',
  ];

  const printLimitsAreDirty = printLimitAttributeKeys.some((key) => {
    const selectedAttribute = attributeTypes[key];

    const prevValue = selectedAttribute?.attribute?.value;

    if (attributesById[selectedAttribute?.attribute?.id]?.value === undefined) {
      return false;
    }

    return attributesById[selectedAttribute?.attribute?.id]?.value !== prevValue;
  });

  return data.length > 0 ? (
    <BindCard>
      {[
        {
          title: 'Excess Options',
          content: (
            <Box sx={{ width: '100%', minWidth: 0 }}>
              <Typography variant='bodySmall'>
                Below are other options for your Excess coverage and the premium the selection would result in. If you
                would like to make a different selection, please select a different checkbox and save.
              </Typography>
              <Grid sx={{ width: '100%', overflowX: 'auto' }}>
                <Table
                  ariaLabel='excess premium options'
                  columns={excessOptionsColumns}
                  onLoadData={() => {
                    return {
                      data: {
                        items: data,
                      },
                      isLoading: false,
                      isFetching: false,
                      isError: false,
                    };
                  }}
                  hidePagination
                  noResultsConfig={{ noResultsMessage: 'No Associated Policies found' }}
                  sxProps={{
                    tableContainer: {
                      minWidth: { xs: '680px', md: '100%' },
                    },
                  }}
                />
              </Grid>
              <Grid container sx={{ mt: 2 }}>
                <Typography variant='bodySmall'>
                  Terrorism is included with your proposal. Please select the checkbox below if you want to remove it,
                  then save.
                </Typography>
                <Grid>
                  <Checkbox
                    checked={!terrorismAcceptance}
                    onChange={() => updateTerrorismAcceptance(!terrorismAcceptance)}
                  />
                  <Typography variant='bodySmall'>I reject Excess Certified Acts of Terrorism </Typography>
                </Grid>
              </Grid>
              <Grid
                container
                justifyContent='flex-end'
                sx={{
                  mt: 2,
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                {isGeneralLiabilityIndOn && (
                  <Button
                    variant='outlined'
                    disabled={disableSaveExcess || isSaveFieldsLoading || isCreateRatingLoading}
                    isLoading={isSaveFieldsLoading || isCreateRatingLoading}
                    onClick={() => handleRemoveExcess()}
                  >
                    Remove Excess
                  </Button>
                )}
                <Button
                  variant='outlined'
                  disabled={
                    disableSaveExcess ||
                    (excessCoverage === attributeTypes.EXEachEventLimit.attribute?.value &&
                      terrorismAcceptance === attributeTypes.EXPolicyTerrorismCoverageInd.attribute?.value &&
                      !printLimitsAreDirty)
                  }
                  isLoading={isSaveFieldsLoading || isCreateRatingLoading}
                  onClick={() => handleSave()}
                >
                  Save
                </Button>
              </Grid>
              {showFieldsAutoChangedWarning && (
                <Typography
                  variant='bodySmall'
                  color='text.subdued'
                  sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}
                >
                  Note: a data update with the latest release requires you to save this panel before bind
                </Typography>
              )}
            </Box>
          ),
        },
      ]}
    </BindCard>
  ) : null;
};
